export type NotificationType =
  | 'PAUSED_MONITORS'
  | 'CARD_EXPIRING'
  | 'PAYMENT_REMINDER'
  | 'PROJECT_READY'
  | 'AI_VISIBILITY_REPORT_READY'
  | 'ADMIN';

export type NotificationLevel = 'INFO' | 'WARNING' | 'CRITICAL';

export interface AppNotification {
  type: NotificationType;
  data: Record<string, unknown>;
  seen: boolean;
  // Severity — drives icon and colour.
  level?: NotificationLevel;
  // Mark-seen key; falls back to `type` for the derived notifications. Admin notifications carry a
  // per-id key so each can be dismissed independently.
  key?: string;
  // Ordering hint from the backend — higher first.
  priority?: number;
}

export interface AdminNotificationTranslation {
  title: string;
  description?: string | null;
  ctaLabel?: string | null;
}

export interface AdminNotificationData {
  id: number;
  translations: {
    en?: AdminNotificationTranslation;
    pl?: AdminNotificationTranslation;
  };
  level: string;
  hasPopup: boolean;
  ctaUrl: string | null;
}

export interface ResolvedAdminNotification {
  title: string;
  description: string | null;
  ctaUrl: string | null;
  ctaLabel: string | null;
}

// Pick the language variant for the current UI language (falls back to the other language).
export const resolveAdminNotification = (
  data: AdminNotificationData,
  lang: string
): ResolvedAdminNotification => {
  const pl = lang.toLowerCase().startsWith('pl');
  const primary = pl ? data.translations.pl : data.translations.en;
  const fallback = pl ? data.translations.en : data.translations.pl;
  return {
    title: primary?.title || fallback?.title || '',
    description: primary?.description ?? fallback?.description ?? null,
    ctaUrl: data.ctaUrl,
    ctaLabel: primary?.ctaLabel ?? fallback?.ctaLabel ?? null,
  };
};

export interface PausedMonitorsNotificationData {
  count: number;
  availableSlots: number;
}

export interface CardExpiringNotificationData {
  expiryCardMonth: number;
  expiryCardYear: number;
}

export interface PaymentReminderNotificationData {
  endsAt: string;
}

export interface ProjectReadyNotificationData {
  projectId: number;
  projectName: string;
}

export interface AiVisibilityReportReadyNotificationData {
  reportId: number;
  projectId: number;
  projectName: string;
  periodFrom: string;
  periodTo: string;
  periodLabel: string;
  reportTitle?: string;
}
